import { useMemo, useState } from 'react'
import EstoqueHttp from '../../../../../services/api/https/estoque/Estoque.http'
import { errorHandler } from '../../../../../services/util/error.service'
import { getPeriodoMesAtual, parseToMysqlFormat } from '../../../../../services/util/date.service'
import { getUsuarioInfo } from '../../../../../services/localstorage/localstorage.service'
import { DateTime } from 'luxon'

function getCurrentTimeString() {
  const now = new Date()
  return `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`
}

function getProdutoMovimentacaoKey(produto) {
  if (!produto.pallet_area_id) return `manual-${produto.id}`

  return [
    produto.pallet_area_id,
    produto.delivery_item_id,
    produto.product_id || produto.id,
    produto.lote || '',
    produto.validade || '',
    produto.data_fabricacao || '',
  ].join('|')
}

function mapProdutoMovimentacao(produto, extra = {}) {
  const nextProduto = { ...produto, ...extra }
  return {
    ...nextProduto,
    rowKey: getProdutoMovimentacaoKey(nextProduto),
  }
}

export const MENU_ITEMS = ['1. Editar', '2. Produtos']

export const TIPO_LISTAGEM_POR_PALLET = { name: 'Por Pallet', value: 'Por Pallet' }

export const LOAD_MESSAGE = {
  NONE: 'NONE',
  PICKINGS: 'Carregando Pickings',
  PALLETAREAS: 'Carregando Pallets Area',
  PALLETAREAS_PRODUTOS: 'Carregando Produtos das Pallets Area',
  PRODUTOS: 'Carregando Produtos',
  POSICOES: 'Carregando posições do estoque',
  MOVING: 'Concluindo operação',
}

const INITIAL_STATE = {
  movimentacoes: [],
  modal: {
    show: false,
    data: null,
  },
  dataMovimentacao: new Date(),
  horaMovimentacao: '',
  pickings: [],
  pallets: [],
  posicoes: [],
  produtos: [],
  periodo: getPeriodoMesAtual(),
  originalPosicoes: [],
  originalPallets: [],
  operacoes: [],
  selectedOperacao: null,
  selectedPicking: null,
  selectedPallets: [],
  selectedProdutos: [],
  selectedPosicao: null,
  selectedTipoListagem: null,
  palletsEstoqueInfo: [],
  globalFilter: '',
  searchPallet: '',
  searchPosicao: '',
  loadingMessage: LOAD_MESSAGE.NONE,
  errorMessage: null,
}

export default function useMovimentacaoEstoque() {
  const [state, setState] = useState(() => ({
    ...INITIAL_STATE,
    horaMovimentacao: getCurrentTimeString(),
  }))

  const localDispatch = useMemo(() => {
    //Helpers

    return {
      //Actions
      resetState() {
        setState(prevState => ({
          ...prevState,
          loadingMessage: LOAD_MESSAGE.NONE,
          errorMessage: null,
          pathToRedirect: null,
        }))
      },

      getOperacoesEstoqueEntrada(selectedTp) {
        EstoqueHttp.getOperacoesEstoqueEntrada(selectedTp)
          .then(res =>
            setState(prevState => ({
              ...prevState,
              operacoes: res.filter(r => r.grupo_de_operacao === 'Recebimento'),
              loadingMessage: LOAD_MESSAGE.NONE,
            })),
          )
          .catch(err =>
            setState(prevState => ({
              ...prevState,
              loadingMessage: LOAD_MESSAGE.NONE,
              errorMessage: errorHandler(err),
            })),
          )
      },

      getPosicoesEstoque(selectedTp, estoqueId) {
        EstoqueHttp.getPosicoesEstoques(selectedTp, estoqueId)
          .then(res =>
            setState(prevState => ({
              ...prevState,
              posicoes: res.filter(p => p.status !== 'Bloqueado'),
              originalPosicoes: res.filter(p => p.status !== 'Bloqueado'),
              loadingMessage: LOAD_MESSAGE.NONE,
            })),
          )
          .catch(err =>
            setState(prevState => ({
              ...prevState,
              loadingMessage: LOAD_MESSAGE.NONE,
              errorMessage: errorHandler(err),
            })),
          )
      },

      getPickings(tpId, pInicial, pFinal) {
        EstoqueHttp.getPickingsByDeliveriesTpId(
          tpId,
          parseToMysqlFormat(pInicial),
          parseToMysqlFormat(pFinal),
        )
          .then(res =>
            setState(prevState => ({
              ...prevState,
              pickings: res,
              loadingMessage: LOAD_MESSAGE.NONE,
            })),
          )
          .catch(err =>
            setState(prevState => ({
              ...prevState,
              loadingMessage: LOAD_MESSAGE.NONE,
              errorMessage: errorHandler(err),
            })),
          )
      },

      getPalletAreas(pickingId) {
        EstoqueHttp.getPalletsAreaByPickingId(pickingId)
          .then(res =>
            setState(prevState => ({
              ...prevState,
              pallets: res.map(p => ({ ...p, selected: false, alocated: false })),
              originalPallets: res.map(p => ({ ...p, selected: false, alocated: false })),
              loadingMessage: LOAD_MESSAGE.NONE,
            })),
          )
          .catch(err =>
            setState(prevState => ({
              ...prevState,
              loadingMessage: LOAD_MESSAGE.NONE,
              errorMessage: errorHandler(err),
            })),
          )
      },

      getProdutos(pickingId) {
        EstoqueHttp.getProdutosByPickingId(pickingId)
          .then(res =>
            setState(prevState => ({
              ...prevState,
              produtos: res.map(p =>
                mapProdutoMovimentacao(p, {
                  alocated: false,
                  validade: '__/__/____',
                  lote: '',
                  qte_alocar: p.qte_total,
                  qte_total_original: p.qte_total,
                }),
              ),
              loadingMessage: LOAD_MESSAGE.NONE,
            })),
          )
          .catch(err =>
            setState(prevState => ({
              ...prevState,
              loadingMessage: LOAD_MESSAGE.NONE,
              errorMessage: errorHandler(err),
            })),
          )
      },

      getPalletAreasProdutos(pickingId) {
        EstoqueHttp.getPalletAreasProdutosByPickingId(pickingId)
          .then(res =>
            setState(prevState => ({
              ...prevState,
              produtos: res.map(p =>
                mapProdutoMovimentacao(p, {
                  alocated: false,
                  qte_alocar: p.qte_total,
                  qte_total_original: p.qte_total,
                }),
              ),
              loadingMessage: LOAD_MESSAGE.NONE,
            })),
          )
          .catch(err =>
            setState(prevState => ({
              ...prevState,
              loadingMessage: LOAD_MESSAGE.NONE,
              errorMessage: errorHandler(err),
            })),
          )
      },

      postMovimentacaoEstoqueRecebimento(
        movimentacaoEstoque,
        movimentacaoEstoqueInfo,
        produtos,
        selectedTp,
        style,
        userId,
      ) {
        EstoqueHttp.postMovimentacaoEstoqueRecebimento(
          { movimentacaoEstoque, movimentacaoEstoqueInfo, produtos, style },
          selectedTp,
          userId,
        )
          .then(res =>
            setState(prevState => ({
              ...prevState,
              pallets:
                style === 'Manual'
                  ? []
                  : res.map(p => ({ ...p, selected: false, alocated: false })),
              produtos:
                style === 'Manual' || style === 'Por Produto'
                  ? res.map(p =>
                      mapProdutoMovimentacao(p, {
                        alocated: false,
                        validade: p.validade,
                        lote: p.lote,
                        qte_alocar: p.qte_total,
                      }),
                    )
                  : [],
              loadingMessage: LOAD_MESSAGE.POSICOES,
            })),
          )
          .catch(err =>
            setState(prevState => ({
              ...prevState,
              loadingMessage: LOAD_MESSAGE.NONE,
              errorMessage: errorHandler(err),
            })),
          )
      },

      //Events
      async onInit(selectedTp) {
        if (!selectedTp) {
          setState(prevState => ({ ...prevState, errorMessage: 'Selecione um TP' }))
        } else {
          await EstoqueHttp.getEstoques(selectedTp)
            .then(res => {
              setState(prevState => ({
                ...prevState,
                estoques: res,
                loadingMessage: LOAD_MESSAGE.NONE,
                posicoes: [],
                produtos: [],
                selectedTipoListagem: null,
                selectedPosicao: null,
                selectedOperacao: null,
                selectedPicking: null,
              }))
            })
            .catch(err =>
              setState(prevState => ({ ...prevState, errorMessage: errorHandler(err) })),
            )
          this.getOperacoesEstoqueEntrada(selectedTp)
        }
      },

      onChange(name, value) {
        setState(prevState => {
          prevState[name] = value
          switch (name) {
            case 'selectedPicking':
              prevState.produtos = []
              prevState.pallets = []
              prevState.selectedPallets = []
              prevState.selectedProdutos = []

              if (!prevState.selectedPicking) {
                prevState.selectedTipoListagem = null
                prevState.loadingMessage = LOAD_MESSAGE.NONE
                break
              }

              if (prevState.selectedPicking.style === 'Codigo de Barra') {
                prevState.selectedTipoListagem = TIPO_LISTAGEM_POR_PALLET
                prevState.loadingMessage = LOAD_MESSAGE.PALLETAREAS
              } else {
                prevState.selectedTipoListagem = null
                prevState.loadingMessage = LOAD_MESSAGE.PRODUTOS
              }
              break
            case 'selectedTipoListagem':
              prevState.selectedTipoListagem = TIPO_LISTAGEM_POR_PALLET
              prevState.produtos = []
              prevState.pallets = []
              prevState.selectedPallets = []
              prevState.selectedProdutos = []
              prevState.loadingMessage = LOAD_MESSAGE.PALLETAREAS
              break
            case 'selectedEstoque':
              prevState.loadingMessage = LOAD_MESSAGE.POSICOES
              break
            default:
              break
          }

          return { ...prevState }
        })
      },

      onChangeProduto(produtoRowKey, value, name) {
        setState(prevState => {
          const index = prevState.produtos.findIndex(p => p.rowKey === produtoRowKey)
          if (index !== -1) {
            prevState.produtos[index][name] = value
          }
          return { ...prevState }
        })
      },

      onBtnPesquisarClick(dataInicial, dataFinal) {
        setState(prevState => ({
          ...prevState,
          periodo: { dataInicial, dataFinal },
          loadingMessage: LOAD_MESSAGE.PICKINGS,
        }))
      },

      onSearchPosicoesChange(value) {
        setState(prevState => {
          prevState.searchPosicao = value

          if (!prevState.originalPosicoes.length) return { ...prevState }

          if (!value.length) prevState.posicoes = prevState.originalPosicoes
          else {
            prevState.posicoes = prevState.originalPosicoes.filter(p =>
              p.endereco.toLowerCase().includes(value.toLowerCase()),
            )
          }

          return { ...prevState }
        })
      },

      onSearchPalletsChange(value) {
        setState(prevState => {
          prevState.searchPallet = value

          if (!prevState.originalPallets.length) return { ...prevState }

          if (!value.length) prevState.pallets = prevState.originalPallets
          else {
            prevState.pallets = prevState.originalPallets.filter(p =>
              p.id.toString().toLowerCase().includes(value.toLowerCase()),
            )
          }

          return { ...prevState }
        })
      },

      onClickPosicao(posicao) {
        setState(prevState => ({ ...prevState, selectedPosicao: posicao }))
      },

      onClickPallets(pallet) {
        setState(prevState => {
          const index = prevState.pallets.findIndex(p => p.id === pallet.id)
          prevState.pallets[index].selected = !prevState.pallets[index].selected

          return { ...prevState }
        })
      },

      onClickAlocar(selectedPallets, produtos, selectedPosicao, style, tipoVisualizer = null) {
        setState(prevState => {
          if (style !== 'Manual' && tipoVisualizer !== 'Por Produto') {
            if (!selectedPallets.length) return { ...prevState }
            if (!selectedPosicao)
              return { ...prevState, errorMessage: 'Selecione uma posição para alocar' }

            prevState.pallets.forEach(p => {
              selectedPallets.forEach(sp => {
                if (p.id === sp.id) {
                  p.selected = false
                  p.alocated = true
                  p.posicao = selectedPosicao
                  p.pos_estoque = selectedPosicao.endereco
                }
                return { ...p }
              })
            })

            prevState.posicoes = prevState.posicoes.map(p => {
              p.pallets = prevState.pallets.filter(
                pal => pal.selected || (pal.alocated && pal.posicao.id === p.id),
              ).length

              return p
            })
          } else {
            if (!produtos.length) return { ...prevState }
            if (!selectedPosicao)
              return { ...prevState, errorMessage: 'Selecione uma posição para alocar' }

            const backupProdutos = [...prevState.produtos.map(p => ({ ...p }))]

            prevState.produtos.forEach(p => {
              prevState.selectedProdutos.forEach(sp => {
                if (sp.rowKey === p.rowKey) {
                  const qteAlocar = Number(p.qte_alocar)

                  if (
                    !Number.isFinite(qteAlocar) ||
                    qteAlocar < 1 ||
                    qteAlocar > Number(p.qte_total)
                  )
                    prevState.errorMessage = `Insira uma quantidade valida para alocar no produto: ${p.name}`
                  else {
                    p.pos_estoque = selectedPosicao.endereco
                    p.qte_alocated = qteAlocar
                    p.qte_total -= Number(p.qte_alocated)
                    p.posicao = selectedPosicao
                    p.alocated = true
                  }
                }
              })
            })

            if (prevState.errorMessage) {
              prevState.selectedProdutos = []
              prevState.produtos = [...backupProdutos]
              return { ...prevState }
            }

            const indexTarget = prevState.posicoes.findIndex(p => p.id === selectedPosicao.id)
            if (indexTarget !== -1) {
              prevState.posicoes[indexTarget].caixas += prevState.selectedProdutos
                .filter(sp => sp.posicao.id === selectedPosicao.id)
                .reduce((curr, prev) => curr + Number(prev.qte_alocated), 0)
            }

            if (!prevState.errorMessage) prevState.selectedProdutos = []
          }

          return { ...prevState }
        })
      },

      validatDataValidade(validade) {
        if (validade.length < 10 || validade.includes('__')) return 'Insira uma data de validade'

        const validadeDatas = validade.split('/').map(Number)

        if (validadeDatas[0] < 1 || validadeDatas > 31) return 'Insira um dia valido'
        else if (validadeDatas[1] < 1 || validadeDatas[1] > 12) return 'Insira um mês valido'
        else if (validadeDatas[2] < 2018 || validadeDatas[2] > 2200) return 'Insira um ano valido'
        return null
      },

      onClickDesalocar(selectedPallets, produtos, style, tipoVisualizer = null) {
        setState(prevState => {
          if (style !== 'Manual' && tipoVisualizer !== 'Por Produto') {
            if (!selectedPallets.length) return { ...prevState }

            prevState.pallets.forEach(p => {
              selectedPallets.forEach(sp => {
                if (p.id === sp.id) {
                  const posIndex = prevState.posicoes.findIndex(pos => pos.id === p.posicao.id)

                  prevState.posicoes[posIndex].pallets--

                  p.selected = false
                  p.alocated = false
                  p.posicao = null
                  p.pos_estoque = null
                }
                return { ...p }
              })
            })
          } else {
            if (!produtos.length) return { ...prevState }

            const selectedPosicoesIds = prevState.produtos
              .filter(p => p.alocated)
              .map(p => p.posicao.id)

            prevState.posicoes.forEach(pos => {
              if (selectedPosicoesIds.includes(pos.id)) {
                const index = prevState.posicoes.findIndex(p => p.id === pos.id)

                if (index !== -1) {
                  const qteTotal = prevState.selectedProdutos
                    .filter(sp => sp.posicao.id === pos.id)
                    .map(sp => sp.qte_alocated)
                    .reduce((curr, prev) => curr + Number(prev), 0)
                  prevState.posicoes[index].caixas -= qteTotal
                }
              }
            })

            prevState.produtos.forEach(p => {
              prevState.selectedProdutos.forEach(sp => {
                if (sp.rowKey === p.rowKey) {
                  p.pos_estoque = ''
                  p.qte_total += Number(p.qte_alocated)
                  p.qte_alocated = 0
                  p.posicao = null
                  p.alocated = false
                }
              })
            })

            prevState.selectedProdutos = []
          }
          return { ...prevState }
        })
      },

      async onClickConcluir(
        pallets,
        produtos,
        data,
        hora,
        operacao,
        picking,
        cycle,
        selectedTp,
        style,
        tipoVisualizer = null,
      ) {
        if (!operacao) {
          setState(prevState => ({
            ...prevState,
            errorMessage: 'Selecione uma operação antes de concluir.',
          }))
          return
        }

        setState(prevState => ({ ...prevState, loadingMessage: LOAD_MESSAGE.MOVING }))

        const movimentacaoEstoque = {
          operacao_estoque_id: operacao.id,
          data_movimentacao: `${parseToMysqlFormat(data)} ${hora || '00:00'}:00`,
          tp_id: selectedTp,
        }

        if (style !== 'Manual' && tipoVisualizer !== 'Por Produto') {
          const palletsAlocated = pallets.filter(p => p.alocated)
          if (!palletsAlocated.length) {
            setState(prevState => ({
              ...prevState,
              errorMessage: 'Você precisar alocar pelo menos um pallet para prosseguir.',
              loadingMessage: LOAD_MESSAGE.NONE,
            }))
            return
          }

          const movimentacoesEstoqueInfo = palletsAlocated.map(p => ({
            posicao_estoque_id: p.posicao.id,
            pallet_area_id: p.id,
            picking_id: picking.id,
            type: 'recebimento',
          }))

          await this.postMovimentacaoEstoqueRecebimento(
            movimentacaoEstoque,
            movimentacoesEstoqueInfo,
            produtos,
            selectedTp,
            'Codigo de Barra',
            getUsuarioInfo().id,
          )
        } else {
          const produtosAlocated = produtos.filter(p => p.alocated)
          if (!produtosAlocated.length) {
            setState(prevState => ({
              ...prevState,
              errorMessage: 'Você precisar alocar pelo menos um produto para prosseguir.',
              loadingMessage: LOAD_MESSAGE.NONE,
            }))
            return
          }
          const posicoesSelecionadas = Array.from(new Set(produtosAlocated.map(p => p.posicao.id)))

          const movimentacoesEstoqueInfo = []

          posicoesSelecionadas.forEach(posId => {
            movimentacoesEstoqueInfo.push({
              posicao_estoque_id: posId,
              picking_id: picking.id,
              type: 'recebimento',
            })
          })

          const produtosToAlocate = produtos
            .filter(p => p.alocated)
            .map(p => ({
              ...p,
              validade:
                p.validade && !p.validade.includes('_')
                  ? DateTime.fromFormat(p.validade, 'dd/MM/yyyy').toFormat('yyyy-MM-dd')
                  : null,
            }))

          await this.postMovimentacaoEstoqueRecebimento(
            movimentacaoEstoque,
            movimentacoesEstoqueInfo,
            produtosToAlocate,
            selectedTp,
            tipoVisualizer === 'Por Produto' ? 'Por Produto' : 'Manual',
            getUsuarioInfo().id,
          )
        }
      },
    }
  }, [])

  return { state, localDispatch }
}
